import React, { useState } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text, LinearGradient, isWeb } from "@xsolla/xui-primitives";
import { useResolvedTheme } from "@xsolla/xui-core";
import type {
  GameCardProps,
  GameCardSize,
  GameCardImageRatio,
  GameCardPlatform,
  GameCardLayout,
} from "./types";

const IMAGE_RATIOS: Record<Exclude<GameCardImageRatio, "custom">, number> = {
  "16:9": 240 / 135,
  "1:1": 1,
  "2:3": 240 / 360,
  "3:2": 240 / 160,
  "4:3": 360 / 270,
  "9:16": 135 / 240,
};

const BORDER_RADIUS = 8;
const TAG_INSET = 8;
const TAG_GAP = 4;

interface SizeConfig {
  titleFontSize: number;
  titleLineHeight: number;
  subtitleFontSize: number;
  subtitleLineHeight: number;
}

const getSizeConfig = (
  size: GameCardSize,
  platform: GameCardPlatform
): SizeConfig => {
  const isDesktop = platform === "desktop";
  switch (size) {
    case "lg":
      return {
        titleFontSize: isDesktop ? 16 : 14,
        titleLineHeight: isDesktop ? 20 : 18,
        subtitleFontSize: isDesktop ? 12 : 11,
        subtitleLineHeight: isDesktop ? 18 : 14,
      };
    case "md":
      return {
        titleFontSize: isDesktop ? 16 : 14,
        titleLineHeight: isDesktop ? 20 : 18,
        subtitleFontSize: isDesktop ? 12 : 11,
        subtitleLineHeight: isDesktop ? 18 : 14,
      };
    case "sm":
      return {
        titleFontSize: 12,
        titleLineHeight: 16,
        subtitleFontSize: 10,
        subtitleLineHeight: 14,
      };
  }
};

const getFooterPadding = (platform: GameCardPlatform): number =>
  platform === "desktop" ? 16 : 12;

const getFooterGap = (): number => 12;

const resolveAspectRatio = (
  imageRatio: GameCardImageRatio,
  customWidth?: number,
  customHeight?: number
): number => {
  if (imageRatio === "custom" && customWidth && customHeight) {
    return customWidth / customHeight;
  }
  return (
    IMAGE_RATIOS[imageRatio as Exclude<GameCardImageRatio, "custom">] ??
    IMAGE_RATIOS["16:9"]
  );
};

const ellipsisStyle = isWeb
  ? {
      overflow: "hidden" as const,
      textOverflow: "ellipsis" as const,
      whiteSpace: "nowrap" as const,
    }
  : undefined;

export const GameCard: React.FC<GameCardProps> = ({
  image,
  title,
  subtitle,
  size = "lg",
  imageRatio = "16:9",
  customImageWidth,
  customImageHeight,
  badge,
  tagsTopLeft,
  tagsTopRight,
  tagsBottomLeft,
  tagsBottomRight,
  trailing,
  buttonText,
  onButtonClick,
  buttonDisabled,
  layout = "horizontal",
  platform = "mobile",
  hideFooter = false,
  hoverContent,
  onPress,
  imageAlt,
  className,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });
  const config = getSizeConfig(size, platform);
  const footerPadding = getFooterPadding(platform);
  const footerGap = getFooterGap();
  const aspectRatio = resolveAspectRatio(
    imageRatio,
    customImageWidth,
    customImageHeight
  );

  const [isHovered, setIsHovered] = useState(false);

  const wrapperProps = onPress ? { onPress } : {};
  const hoverHandlers =
    isWeb && hoverContent
      ? {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
        }
      : {};

  const isHorizontal = layout === "horizontal";

  const renderPlaceholder = () => (
    <Box
      width="100%"
      height="100%"
      backgroundColor={theme.colors.background.secondary}
      alignItems="center"
      justifyContent="center"
    >
      <Box
        width={
          imageRatio === "16:9" ||
          imageRatio === "9:16" ||
          imageRatio === "custom"
            ? 48
            : 56
        }
        height={
          imageRatio === "16:9" ||
          imageRatio === "9:16" ||
          imageRatio === "custom"
            ? 48
            : 56
        }
        alignItems="center"
        justifyContent="center"
        style={{ opacity: 0.4 }}
      >
        <Text
          color={theme.colors.content.tertiary}
          fontSize={24}
          style={{ textAlign: "center" as const }}
        >
          {"\u{1F5BC}"}
        </Text>
      </Box>
    </Box>
  );

  const renderImage = () => (
    <Box
      position="relative"
      width="100%"
      overflow="hidden"
      style={{ aspectRatio }}
      {...hoverHandlers}
    >
      {image ? (
        <>
          <Box
            as="img"
            src={image}
            alt={imageAlt || title}
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            style={
              isWeb
                ? {
                    objectFit: "cover" as const,
                    pointerEvents: "none" as const,
                  }
                : undefined
            }
          />
          <LinearGradient
            colors={["transparent", "rgba(0, 0, 0, 0.48)"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            height="100%"
            style={{ maxHeight: 206 }}
          />
        </>
      ) : (
        renderPlaceholder()
      )}

      {/* Tag zones */}
      {(badge || tagsTopLeft) && (
        <Box
          position="absolute"
          top={TAG_INSET}
          left={TAG_INSET}
          flexDirection="column"
          gap={TAG_GAP}
          alignItems="flex-start"
        >
          {badge}
          {tagsTopLeft}
        </Box>
      )}

      {tagsTopRight && (
        <Box
          position="absolute"
          top={TAG_INSET}
          right={TAG_INSET}
          flexDirection="column"
          gap={TAG_GAP}
          alignItems="flex-end"
        >
          {tagsTopRight}
        </Box>
      )}

      {tagsBottomLeft && (
        <Box
          position="absolute"
          bottom={TAG_INSET}
          left={TAG_INSET}
          flexDirection="column"
          gap={TAG_GAP}
          alignItems="flex-start"
        >
          {tagsBottomLeft}
        </Box>
      )}

      {tagsBottomRight && (
        <Box
          position="absolute"
          bottom={TAG_INSET}
          right={TAG_INSET}
          flexDirection="column"
          gap={TAG_GAP}
          alignItems="flex-end"
        >
          {tagsBottomRight}
        </Box>
      )}

      {/* Hover overlay */}
      {isWeb && hoverContent && isHovered && (
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          height={72}
          flexDirection="row"
          alignItems="center"
          gap={8}
          padding={16}
          borderRadius={4}
          overflow="hidden"
          style={{
            backdropFilter: "blur(15px)",
            WebkitBackdropFilter: "blur(15px)",
            backgroundColor: theme.colors.layer.float,
          }}
        >
          {hoverContent}
        </Box>
      )}
    </Box>
  );

  const renderFooter = () => (
    <Box
      backgroundColor={theme.colors.overlay.mono}
      padding={footerPadding}
      flexDirection={isHorizontal ? "row" : "column"}
      alignItems={isHorizontal ? "center" : "flex-start"}
      justifyContent={isHorizontal ? undefined : "center"}
      gap={footerGap}
    >
      <Box
        flex={isHorizontal ? 1 : undefined}
        width={isHorizontal ? undefined : "100%"}
        flexDirection="column"
        style={{ minWidth: 0 }}
      >
        <Text
          color={theme.colors.content.primary}
          fontSize={config.titleFontSize}
          lineHeight={config.titleLineHeight}
          fontWeight="500"
          numberOfLines={1}
          style={ellipsisStyle}
        >
          {title}
        </Text>
        {subtitle && (
          <Text
            color={theme.colors.content.tertiary}
            fontSize={config.subtitleFontSize}
            lineHeight={config.subtitleLineHeight}
            fontWeight="400"
            numberOfLines={1}
            style={ellipsisStyle}
          >
            {subtitle}
          </Text>
        )}
      </Box>

      {trailing}
      {!trailing && buttonText && (
        <Box
          onPress={buttonDisabled ? undefined : onButtonClick}
          backgroundColor={theme.colors.control.mono.secondary.bg}
          borderRadius={BORDER_RADIUS}
          height={40}
          paddingHorizontal={20}
          alignItems="center"
          justifyContent="center"
          style={
            isWeb
              ? {
                  opacity: buttonDisabled ? 0.4 : 1,
                  backdropFilter: "blur(30px)",
                  WebkitBackdropFilter: "blur(30px)",
                  border: `1px solid ${theme.colors.control.mono.secondary.border}`,
                  cursor: buttonDisabled
                    ? "not-allowed"
                    : onButtonClick
                      ? "pointer"
                      : "default",
                }
              : { opacity: buttonDisabled ? 0.4 : 1 }
          }
        >
          <Text
            color={theme.colors.control.mono.text.secondary}
            fontSize={14}
            lineHeight={18}
            fontWeight="500"
            style={{ letterSpacing: 0.4 }}
          >
            {buttonText}
          </Text>
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      testID={testID}
      className={className}
      flexDirection="column"
      width="100%"
      {...wrapperProps}
    >
      <Box
        borderRadius={BORDER_RADIUS}
        overflow="hidden"
        flexDirection="column"
        style={
          isWeb
            ? {
                borderRadius: `${BORDER_RADIUS}px`,
                overflow: "hidden",
                border: `1px solid ${theme.colors.border.secondary}`,
              }
            : {
                borderRadius: BORDER_RADIUS,
                overflow: "hidden",
                borderWidth: 1,
                borderColor: theme.colors.border.secondary,
              }
        }
      >
        {renderImage()}
        {!hideFooter && renderFooter()}
      </Box>
    </Box>
  );
};
